import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "border border-input bg-card shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 gap-1.5 px-3 py-1.5",
        lg: "h-10 px-8",
        icon: "h-8 w-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonBaseProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  Omit<VariantProps<typeof buttonVariants>, "size"> & {
    asChild?: boolean;
    /**
     * Hover hint. Renders the `Tooltip` component — never the native `title`
     * attribute, which the browser delays ~1.5s and refuses to style, so it
     * reads as broken. Needs a `TooltipProvider` above it; the app root and the
     * Storybook decorator both supply one.
     */
    tooltip?: React.ReactNode;
    /**
     * Which side of the button the tooltip opens on. Defaults to `top`, which is
     * right for a horizontal toolbar and wrong for a vertical one: there the
     * tooltip covers the button above, so moving the pointer up lands it inside
     * the tooltip instead of on the next control. Vertical rails pass `right`.
     */
    tooltipSide?: React.ComponentPropsWithoutRef<typeof TooltipContent>["side"];
    /**
     * The action is running. Disables the button and puts a spinner before the
     * label, which stays as it is: a save button reads "Save" the whole time,
     * never "Saving..." (QL-223). An icon button swaps its glyph for the spinner,
     * since two glyphs do not fit the square. Ignored with `asChild`, where the
     * child owns its content.
     */
    loading?: boolean;
  };

type ButtonSize = NonNullable<VariantProps<typeof buttonVariants>["size"]>;

/**
 * An icon button has no text, so the tooltip is the only thing that says what it
 * does — for a sighted user on hover, and for a screen reader as the accessible
 * name, which `Button` derives from a string tooltip. That is why it is required
 * here and optional everywhere else: a labelled button already reads fine
 * without one, and a tooltip that restates the visible label is noise.
 *
 * If a button is glyph-only, give it `size="icon"` rather than squaring off a
 * `sm` with `h-8 w-8` — the square size is what makes this rule reachable.
 */
type IconButtonProps = ButtonBaseProps & {
  size: "icon";
  /**
   * A plain string, not a node: this is the button's accessible name as well as
   * its hover hint, and a node cannot be one. If a hint genuinely needs markup,
   * use the `Tooltip` component directly and give the button its own
   * `aria-label`.
   */
  tooltip: string;
};

type TextButtonProps = ButtonBaseProps & {
  size?: Exclude<ButtonSize, "icon">;
};

export type ButtonProps = IconButtonProps | TextButtonProps;

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      tooltip,
      tooltipSide,
      loading = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const busy = loading && !asChild;
    const isDisabled = disabled || busy;
    const spinner = <Loader2 className="animate-spin" aria-hidden="true" />;
    const button = (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
        disabled={isDisabled}
        aria-busy={busy || undefined}
        // Set after the spread, or an `aria-label: undefined` key in props would
        // clobber it. An icon button has no text node, so without this it
        // announces as "button" and nothing else; an explicit label still wins.
        // Only for size="icon": on a button that already has visible text, an
        // aria-label built from the tooltip would replace that text as the
        // accessible name, so "Export CSV" would stop matching both the screen
        // reader announcement and a voice-control "click Export CSV".
        aria-label={
          props["aria-label"] ??
          (size === "icon" && typeof tooltip === "string" ? tooltip : undefined)
        }
      >
        {busy ? (
          size === "icon" ? (
            spinner
          ) : (
            <>
              {spinner}
              {children}
            </>
          )
        ) : (
          children
        )}
      </Comp>
    );

    if (!tooltip) return button;

    // A disabled button has `pointer-events: none` and gets no hover or focus,
    // so the tooltip — often the only explanation of *why* it is disabled —
    // would never open. The wrapper takes the hover and keyboard focus instead.
    const trigger =
      isDisabled && !asChild ? (
        // biome-ignore lint/a11y/noNoninteractiveTabindex: focus target for the disabled button's explanation
        <span className="inline-flex cursor-not-allowed" tabIndex={0}>
          {button}
        </span>
      ) : (
        button
      );

    return (
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side={tooltipSide}>{tooltip}</TooltipContent>
      </Tooltip>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
